// Imported from the source module rather than the `@/services/api` barrel, for the same
// api-barrel <-> services/auth import cycle `auth-resolution-error.ts` avoids.
import { getApiClientErrorBody, getApiClientErrorDetail, getApiClientErrorStatus } from '@/services/api/errors';
import type { AugmentedAccount, AugmentedUser } from './types';

type TwoFactorSetupAccount = Pick<AugmentedAccount, 'two_factor_authentication_required' | 'is_masq'>;
type TwoFactorSetupUser = Pick<AugmentedUser, 'two_factor_authentication_enabled'>;

const TWO_FACTOR_NOT_ENABLED_DETAIL = 'unauthorized: two-factor authentication not enabled';

/**
 * Whether this user must enroll in two-factor authentication before using the
 * app: their account requires it and they have not turned it on. A masquerading
 * session never enrolls, since minting codes there would replace the ones the
 * real user saved, and the API does not hold masquerade to the requirement.
 */
export function needsTwoFactorSetup(account: TwoFactorSetupAccount, user: TwoFactorSetupUser): boolean {
  return account.two_factor_authentication_required && !user.two_factor_authentication_enabled && !account.is_masq;
}

/**
 * Whether the API refused a request because the account requires two-factor
 * authentication and this user has not enabled it. The session is valid, so
 * re-entering credentials cannot clear this 401; only enrolling does.
 */
export function isTwoFactorSetupRejection(error: unknown): boolean {
  return (
    getApiClientErrorStatus(error) === 401 &&
    getApiClientErrorDetail(getApiClientErrorBody(error)) === TWO_FACTOR_NOT_ENABLED_DETAIL
  );
}
